/**
 * Preview Navigation Guard Script
 *
 * Prevents "inception" — when preview iframe content tries to navigate to external URLs
 * (especially tasking.tech), opening the entire app inside the preview window.
 *
 * Strategy: Intercept ALL navigation at 9 layers:
 * 1. history.pushState / replaceState
 * 2. Navigation API (navigate())
 * 3. <a> click events
 * 4. <form> submit events
 * 5. postMessage navigation hooks
 * 6. Location.prototype.assign / replace / reload
 * 7. window.location setter trap
 * 8. window.open override
 * 9. MutationObserver for meta-refresh and base tags
 */

export const PREVIEW_NAV_GUARD_SCRIPT = `
<script>
(function initPreviewGuard() {
  if (typeof window === 'undefined') return;
  if (window.__PREVIEW_GUARD_ACTIVE) return; // Prevent double-initialization
  window.__PREVIEW_GUARD_ACTIVE = true;

  // Layer 1: history.pushState and history.replaceState
  // Allow SPA routers to call pushState/replaceState — they need this for internal route
  // changes. In srcdoc iframes these throw SecurityError naturally; we catch silently.
  // External absolute URLs are blocked; relative paths are allowed for SPA routing.
  const _origPushState = History.prototype.pushState;
  const _origReplaceState = History.prototype.replaceState;

  function _isExternalUrl(url) {
    if (!url) return false;
    var s = String(url);
    return /^https?:\\/\\//i.test(s);
  }

  History.prototype.pushState = function(state, title, url) {
    if (_isExternalUrl(url)) {
      console.debug('[preview-guard] Blocked pushState to external:', url);
      try { window.parent.postMessage({ type: 'preview-nav-attempt', url: String(url) }, '*'); } catch(e) {}
      return undefined;
    }
    try { return _origPushState.call(this, state, title, url); } catch(e) {
      console.debug('[preview-guard] pushState failed (expected in srcdoc):', e.message);
      return undefined;
    }
  };

  History.prototype.replaceState = function(state, title, url) {
    if (_isExternalUrl(url)) {
      console.debug('[preview-guard] Blocked replaceState to external:', url);
      try { window.parent.postMessage({ type: 'preview-nav-attempt', url: String(url) }, '*'); } catch(e) {}
      return undefined;
    }
    try { return _origReplaceState.call(this, state, title, url); } catch(e) {
      console.debug('[preview-guard] replaceState failed (expected in srcdoc):', e.message);
      return undefined;
    }
  };

  // Layer 2: Navigation API
  if (window.navigation && typeof window.navigation.navigate === 'function') {
    const _origNavigate = window.navigation.navigate;
    window.navigation.navigate = function(url, options) {
      console.debug('[preview-guard] Blocked Navigation.navigate:', url);
      try { window.parent.postMessage({ type: 'preview-nav-attempt', url: String(url) }, '*'); } catch(e) {}
      return Promise.reject(new Error('Navigation blocked by preview guard'));
    };
  }

  // Layer 3: <a> click interception
  // Allow internal SPA route changes (relative paths, hash links) — SPA routers
  // need these clicks to propagate so they can handle route changes.
  // Only block external absolute URL navigation (http/https).
  document.addEventListener('click', function(evt) {
    const target = evt.target.closest('a');
    if (!target) return;

    // Use raw attribute — target.href resolves to "about:srcdoc#id" in srcdoc iframes
    const rawHref = target.getAttribute('href');
    if (!rawHref) return;

    // Allow hash/anchor navigation (e.g., #section) and manually scroll as backup
    if (rawHref.startsWith('#')) {
      const id = rawHref.slice(1);
      if (id) {
        const el = document.getElementById(id);
        if (el) {
          evt.preventDefault();
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
      return;
    }

    // Allow javascript:, mailto:, tel: links
    if (/^(javascript|mailto|tel|sms|data):/i.test(rawHref)) return;

    // Allow relative paths — these are internal SPA routes
    // Relative: starts with /, ./, ../, or doesn't start with a protocol
    if (!_isExternalUrl(rawHref)) {
      // Let the click propagate to SPA router handlers (bubbling phase)
      console.debug('[preview-guard] Allowing SPA route click:', rawHref);
      return;
    }

    // Block external absolute URL navigation
    evt.preventDefault();
    evt.stopPropagation();
    console.debug('[preview-guard] Blocked <a> click to external:', rawHref);
    try { window.parent.postMessage({ type: 'preview-nav-attempt', url: target.href || rawHref }, '*'); } catch(e) {}
  }, true); // Use capture phase to intercept before event handlers

  // Layer 4: <form> submit interception
  document.addEventListener('submit', function(evt) {
    const form = evt.target.closest('form');
    if (!form) return;

    const action = form.action || '';
    if (!action || action === '#') return; // Allow form resets/hash forms

    evt.preventDefault();
    evt.stopPropagation();
    console.debug('[preview-guard] Blocked <form> submit to:', action);
    try { window.parent.postMessage({ type: 'preview-nav-attempt', url: action }, '*'); } catch(e) {}
  }, true); // Capture phase

  // Layer 5: postMessage navigation (iframes trying to navigate parent)
  const _origPostMessage = window.postMessage;
  window.postMessage = function(message, targetOrigin, transfer) {
    if (typeof message === 'object' && message !== null && message.type === 'navigate') {
      console.debug('[preview-guard] Blocked postMessage navigation:', message);
      return;
    }
    return _origPostMessage.call(window, message, targetOrigin, transfer);
  };

  // Layer 6: Location.prototype.assign and replace
  const _origAssign = Location.prototype.assign;
  const _origReplace = Location.prototype.replace;
  const _origReload = Location.prototype.reload;

  Location.prototype.assign = function(url) {
    var s = String(url);
    if (_isExternalUrl(s)) {
      console.debug('[preview-guard] Blocked Location.assign to external:', s);
      try { window.parent.postMessage({ type: 'preview-nav-attempt', url: s }, '*'); } catch(e) {}
      return undefined;
    }
    console.debug('[preview-guard] Allowing Location.assign:', s);
    try { return _origAssign.call(this, url); } catch(e) {
      console.debug('[preview-guard] Location.assign failed (expected in srcdoc):', e.message);
    }
  };

  Location.prototype.replace = function(url) {
    var s = String(url);
    if (_isExternalUrl(s)) {
      console.debug('[preview-guard] Blocked Location.replace to external:', s);
      try { window.parent.postMessage({ type: 'preview-nav-attempt', url: s }, '*'); } catch(e) {}
      return undefined;
    }
    console.debug('[preview-guard] Allowing Location.replace:', s);
    try { return _origReplace.call(this, url); } catch(e) {
      console.debug('[preview-guard] Location.replace failed (expected in srcdoc):', e.message);
    }
  };

  Location.prototype.reload = function() {
    console.debug('[preview-guard] Allowing Location.reload');
    try { return _origReload.call(this); } catch(e) {
      console.debug('[preview-guard] Location.reload failed (expected in srcdoc):', e.message);
    }
  };

  // Layer 7: window.location setter trap
  // Note: In srcdoc iframes, location is non-configurable, so we wrap this in a try-catch
  try {
    const _origLocationDescriptor = Object.getOwnPropertyDescriptor(window, 'location');
    Object.defineProperty(window, 'location', {
      configurable: true,
      get: function() {
        return _origLocationDescriptor.get.call(this);
      },
      set: function(value) {
        var s = String(value);
        if (_isExternalUrl(s)) {
          console.debug('[preview-guard] Blocked window.location setter to external:', s);
          try { window.parent.postMessage({ type: 'preview-nav-attempt', url: s }, '*'); } catch(e) {}
          return;
        }
        console.debug('[preview-guard] Allowing window.location setter:', s);
        try { _origLocationDescriptor.set.call(this, value); } catch(e) {
          console.debug('[preview-guard] location setter failed (expected in srcdoc):', e.message);
        }
      }
    });
  } catch(e) {
    console.debug('[preview-guard] location property is non-configurable (expected in srcdoc):', e.message);
  }

  // Layer 8: window.open override
  const _origOpen = window.open;
  window.open = function(url, target, features) {
    console.debug('[preview-guard] Blocked window.open:', url, target);
    try { window.parent.postMessage({ type: 'preview-nav-attempt', url: String(url), target: target }, '*'); } catch(e) {}
    // Return a mock window object so code doesn't break
    return { close: function() {}, focus: function() {} };
  };

  // Layer 9: MutationObserver for meta-refresh and base tags
  const _observer = new MutationObserver(function(mutations) {
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Remove <base> tags
            if (node.tagName === 'BASE') {
              console.debug('[preview-guard] Removed <base> tag');
              node.remove();
            }
            // Remove <meta http-equiv="refresh"> tags
            if (node.tagName === 'META' && node.hasAttribute('http-equiv')) {
              const httpEquiv = node.getAttribute('http-equiv');
              if (httpEquiv && httpEquiv.toLowerCase() === 'refresh') {
                console.debug('[preview-guard] Removed <meta refresh> tag');
                node.remove();
              }
            }
          }
        }
      }
      if (mutation.type === 'attributes' && mutation.target.tagName === 'BASE') {
        console.debug('[preview-guard] Blocked <base> attribute change');
        mutation.target.remove();
      }
    }
  });

  _observer.observe(document, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['href', 'http-equiv'],
  });

  console.debug('[preview-guard] 9-layer inception guard initialized');
})();
</script>
`;
